import { useMemo } from 'react';
import {
  startOfYear,
  endOfYear,
  eachDayOfInterval,
  format,
  getDay,
  isToday,
  getMonth,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { useApp } from '../context/AppContext';
import type { DayEntry } from '../types';

export interface CalendarDay {
  date: Date;
  dateString: string;
  dayOfMonth: number;
  dayOfWeek: number;
  isWeekend: boolean;
  isToday: boolean;
  entry: DayEntry | null;
}

export interface CalendarMonth {
  month: number;
  year: number;
  name: string;
  days: CalendarDay[];
  startOffset: number; // Wochentag des ersten Tags (0 = So, 1 = Mo, ...)
}

export function buildCalendarMonths(year: number, entries: Record<string, DayEntry>): { days: CalendarDay[]; months: CalendarMonth[] } {
  const start = startOfYear(new Date(year, 0, 1));
  const end = endOfYear(new Date(year, 0, 1));
  const allDays = eachDayOfInterval({ start, end });

  const days: CalendarDay[] = allDays.map((date) => {
    const dateString = format(date, 'yyyy-MM-dd');
    const dayOfWeek = getDay(date);

    return {
      date,
      dateString,
      dayOfMonth: date.getDate(),
      dayOfWeek,
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
      isToday: isToday(date),
      entry: entries[dateString] || null,
    };
  });

  // Gruppiere nach Monaten
  const months: CalendarMonth[] = [];
  for (let m = 0; m < 12; m++) {
    const monthDays = days.filter((d) => getMonth(d.date) === m);
    const firstDay = monthDays[0]!;

    // Montag = 0, Sonntag = 6 (deutsche Woche)
    let startOffset = firstDay.dayOfWeek - 1;
    if (startOffset < 0) startOffset = 6; // Sonntag

    months.push({
      month: m + 1,
      year,
      name: format(new Date(year, m, 1), 'MMMM', { locale: de }),
      days: monthDays,
      startOffset,
    });
  }

  return { days, months };
}

export function useCalendarData() {
  const { state } = useApp();
  const { year, entries } = state;

  const calendarData = useMemo(() => buildCalendarMonths(year, entries), [year, entries]);

  return calendarData;
}

export function useMonthDays(month: number) {
  const { months } = useCalendarData();
  return months.find((m) => m.month === month) || null;
}
